//frontend/src/admin/Settings.js
import React, { useState, useEffect } from 'react';
import { 
  FaSave, FaTimes, FaGlobe, FaImage, FaUpload, 
  FaFacebook, FaBuilding, FaFileAlt, FaCog,
  FaReceipt, FaUniversity, FaPhone
} from 'react-icons/fa';
import axiosInstance from '../axios';
import { useAuth } from '../context/AuthContext';
import '../styles/admin.css';
import '../styles/custom.css';

const Settings = () => {
  const { user } = useAuth();
  
  // ✅ Эрх шалгах функц
  const hasPermission = (permission) => {
    if (!user || !Array.isArray(user.perms)) return false;
    if (user.role === 'system_admin') return true;
    return user.perms.includes(permission);
  };

  // ✅ Харах эрх шалгах
  const canView = hasPermission('settings:view');
  // ✅ Засах эрх шалгах
  const canEdit = hasPermission('settings:edit');

  const [activeTab, setActiveTab] = useState('client');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null);
  
  const [clientSettings, setClientSettings] = useState({
    client_logo: '',
    client_company_name: '',
    client_description: '',
    client_facebook_url: '',
    client_website_url: ''
  });
  
  const [adminSettings, setAdminSettings] = useState({
    admin_logo: '',
    admin_company_name: ''
  });

  // 🆕 Баримтын мэдээлэл (утас + банк)
  const [receiptInfo, setReceiptInfo] = useState({
    receipt_phone_korea_1: '',
    receipt_phone_korea_2: '',
    receipt_phone_korea_3: '',
    receipt_phone_mongolia_1: '',
    receipt_phone_mongolia_2: '',
    receipt_phone_mongolia_3: '',
    receipt_phone_other_1: '',
    receipt_phone_other_2: '',
    receipt_phone_other_3: '',
    receipt_bank1_name: '',
    receipt_bank1_account: '',
    receipt_bank2_name: '',
    receipt_bank2_account: '',
    receipt_account_holder: ''
  });

  useEffect(() => {
    if (canView) {
      fetchSettings();
    }
  }, [canView]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      // Үндсэн тохиргоо
      const response = await axiosInstance.get(`/api/settings?_=${Date.now()}`);
      if (response.data.success) {
        const settings = {};
        response.data.data.forEach(item => {
          settings[item.setting_key] = item.setting_value;
        });
        
        // Client settings
        setClientSettings({
          client_logo: settings.client_logo || '',
          client_company_name: settings.client_company_name || '',
          client_description: settings.client_description || '',
          client_facebook_url: settings.client_facebook_url || '',
          client_website_url: settings.client_website_url || ''
        });
        
        // Admin settings
        setAdminSettings({
          admin_logo: settings.admin_logo || '',
          admin_company_name: settings.admin_company_name || ''
        });

        // 🆕 Receipt settings (утас + банк)
        setReceiptInfo({
          receipt_phone_korea_1: settings.receipt_phone_korea_1 || '',
          receipt_phone_korea_2: settings.receipt_phone_korea_2 || '',
          receipt_phone_korea_3: settings.receipt_phone_korea_3 || '',
          receipt_phone_mongolia_1: settings.receipt_phone_mongolia_1 || '',
          receipt_phone_mongolia_2: settings.receipt_phone_mongolia_2 || '',
          receipt_phone_mongolia_3: settings.receipt_phone_mongolia_3 || '',
          receipt_phone_other_1: settings.receipt_phone_other_1 || '',
          receipt_phone_other_2: settings.receipt_phone_other_2 || '',
          receipt_phone_other_3: settings.receipt_phone_other_3 || '',
          receipt_bank1_name: settings.receipt_bank1_name || '',
          receipt_bank1_account: settings.receipt_bank1_account || '',
          receipt_bank2_name: settings.receipt_bank2_name || '',
          receipt_bank2_account: settings.receipt_bank2_account || '',
          receipt_account_holder: settings.receipt_account_holder || ''
        });
      }
    } catch (error) {
      console.error('Fetch settings error:', error);
      alert('Тохиргоо татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, type) => {
    // ✅ Засах эрх шалгах
    if (!canEdit) {
      alert('Та тохиргоо засах эрхгүй байна');
      return;
    }

    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Зөвхөн зураг файл сонгоно уу!');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      alert('Зургийн хэмжээ 5MB-с бага байх ёстой!');
      return;
    }
    
    setUploadingImage(type);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      const response = await axiosInstance.post('/api/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        if (type === 'client_logo') {
          setClientSettings(prev => ({ ...prev, client_logo: response.data.imageUrl }));
        } else if (type === 'admin_logo') {
          setAdminSettings(prev => ({ ...prev, admin_logo: response.data.imageUrl }));
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(error.response?.data?.msg || 'Зураг хуулахад алдаа гарлаа');
    } finally {
      setUploadingImage(null);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    // ✅ Засах эрх шалгах
    if (!canEdit) {
      alert('Та тохиргоо засах эрхгүй байна');
      return;
    }

    setSaving(true);
    try {
      const allSettings = [
        ...Object.entries(clientSettings).map(([key, value]) => ({ key, value })),
        ...Object.entries(adminSettings).map(([key, value]) => ({ key, value })),
        ...Object.entries(receiptInfo).map(([key, value]) => ({ key, value })) // 🆕 Баримтын мэдээлэл
      ];
      
      const response = await axiosInstance.put('/api/settings', {
        settings: allSettings
      });
      
      if (response.data.success) {
        alert('✅ Тохиргоо амжилттай хадгалагдлаа!');
        window.location.reload();
      }
    } catch (error) {
      console.error('Save settings error:', error);
      alert('❌ Тохиргоо хадгалахад алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  // ✅ Харах эрхгүй бол мессеж харуулах
  if (!canView) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-bold">Хандах эрхгүй</p>
            <p className="text-sm">Та тохиргоо харах эрхгүй байна.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Ачааллаж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 flex items-center">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2 rounded-lg mr-3">
            <FaCog className="text-white text-2xl" />
          </div>
          Тохиргоо
          {/* ✅ Read-only режим мэдээлэл */}
          {canView && !canEdit && (
            <span className="ml-4 text-sm bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full">
              Зөвхөн харах горим
            </span>
          )}
        </h1>
        <p className="text-sm text-gray-600">Системийн үндсэн тохиргоо</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-t-xl border-b overflow-x-auto">
        <div className="flex whitespace-nowrap">
          <button
            onClick={() => setActiveTab('client')}
            className={`px-4 md:px-6 py-3 md:py-4 font-semibold transition-all text-sm md:text-base ${
              activeTab === 'client'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
            }`}
          >
            <FaGlobe className="inline mr-2" />
            Клиент вэб
          </button>
          <button
            onClick={() => setActiveTab('admin')}
            className={`px-4 md:px-6 py-3 md:py-4 font-semibold transition-all text-sm md:text-base ${
              activeTab === 'admin'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
            }`}
          >
            <FaBuilding className="inline mr-2" />
            Админ панел
          </button>
          <button
            onClick={() => setActiveTab('receipt')}
            className={`px-4 md:px-6 py-3 md:py-4 font-semibold transition-all text-sm md:text-base ${
              activeTab === 'receipt'
                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
            }`}
          >
            <FaReceipt className="inline mr-2" />
            Баримтын мэдээлэл
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'client' && (
        <div className="bg-white rounded-b-xl shadow-lg p-4 md:p-8">
          <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-6 border-b pb-3 flex items-center">
            <FaGlobe className="mr-2 text-blue-600" />
            Клиент вэб тохиргоо
          </h2>
          
          <div className="space-y-6">
            {/* Logo Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-6 hover:border-blue-400 transition-all">
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-3">
                Логоны зураг
              </label>
              {clientSettings.client_logo ? (
                <div className="relative inline-block">
                  <img 
                    src={clientSettings.client_logo} 
                    alt="Client Logo" 
                    className="h-16 md:h-24 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                  />
                  {canEdit && (
                    <button
                      onClick={() => setClientSettings(prev => ({ ...prev, client_logo: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-all shadow-md"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <label className={`cursor-pointer flex flex-col items-center px-4 py-3 bg-white text-blue-600 rounded-lg shadow-md tracking-wide border border-blue-600 hover:bg-blue-600 hover:text-white transition-all ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <FaUpload className="w-6 h-6 md:w-8 md:h-8" />
                    <span className="mt-2 text-xs md:text-base">
                      {uploadingImage === 'client_logo' ? 'Хуулж байна...' : 'Зураг сонгох'}
                    </span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'client_logo')}
                      disabled={!canEdit || uploadingImage === 'client_logo'}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2">
                Компанийн нэр
              </label>
              <input
                type="text"
                value={clientSettings.client_company_name}
                onChange={(e) => setClientSettings(prev => ({ ...prev, client_company_name: e.target.value }))}
                className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Жишээ: Жимгүн Карго ХХК"
                disabled={!canEdit}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2">
                Тайлбар
              </label>
              <textarea
                value={clientSettings.client_description}
                onChange={(e) => setClientSettings(prev => ({ ...prev, client_description: e.target.value }))}
                className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                rows="4"
                placeholder="Компанийн товч танилцуулга..."
                disabled={!canEdit}
              />
            </div>

            {/* Facebook URL */}
            <div>
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2 flex items-center">
                <FaFacebook className="mr-2 text-blue-600" />
                Facebook холбоос
              </label>
              <input
                type="url"
                value={clientSettings.client_facebook_url}
                onChange={(e) => setClientSettings(prev => ({ ...prev, client_facebook_url: e.target.value }))}
                className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="https://facebook.com/yourpage"
                disabled={!canEdit}
              />
            </div>

            {/* Website URL */}
            <div>
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2 flex items-center">
                <FaGlobe className="mr-2 text-green-600" />
                Вэбсайт холбоос
              </label>
              <input
                type="url"
                value={clientSettings.client_website_url}
                onChange={(e) => setClientSettings(prev => ({ ...prev, client_website_url: e.target.value }))}
                className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="https://yourwebsite.com"
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'admin' && (
        <div className="bg-white rounded-b-xl shadow-lg p-4 md:p-8">
          <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-6 border-b pb-3 flex items-center">
            <FaBuilding className="mr-2 text-blue-600" />
            Админ панел тохиргоо
          </h2>
          
          <div className="space-y-6">
            {/* Logo Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-6 hover:border-blue-400 transition-all">
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-3">
                Логоны зураг
              </label>
              {adminSettings.admin_logo ? (
                <div className="relative inline-block">
                  <img 
                    src={adminSettings.admin_logo} 
                    alt="Admin Logo" 
                    className="h-16 md:h-24 object-contain border border-gray-300 rounded-lg p-2 bg-white"
                  />
                  {canEdit && (
                    <button
                      onClick={() => setAdminSettings(prev => ({ ...prev, admin_logo: '' }))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-all shadow-md"
                    >
                      <FaTimes className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <label className={`cursor-pointer flex flex-col items-center px-4 py-3 bg-white text-blue-600 rounded-lg shadow-md tracking-wide border border-blue-600 hover:bg-blue-600 hover:text-white transition-all ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <FaUpload className="w-6 h-6 md:w-8 md:h-8" />
                    <span className="mt-2 text-xs md:text-base">
                      {uploadingImage === 'admin_logo' ? 'Хуулж байна...' : 'Зураг сонгох'}
                    </span>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'admin_logo')}
                      disabled={!canEdit || uploadingImage === 'admin_logo'}
                    />
                  </label>
                </div>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm md:text-base font-semibold text-gray-700 mb-2">
                Компанийн нэр
              </label>
              <input
                type="text"
                value={adminSettings.admin_company_name}
                onChange={(e) => setAdminSettings(prev => ({ ...prev, admin_company_name: e.target.value }))}
                className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="Жишээ: Жимгүн Карго ХХК"
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'receipt' && (
        <div className="bg-white rounded-b-xl shadow-lg p-4 md:p-8">
          <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-6 border-b pb-3 flex items-center">
            <FaReceipt className="mr-2 text-green-600" />
            Баримтын мэдээлэл
          </h2>

          <div className="space-y-6">
            {/* УТАСНЫ ДУГААР */}
            <div className="border-2 border-green-100 rounded-xl p-4 md:p-6 bg-gradient-to-br from-green-50 to-white">
              <h3 className="text-base md:text-lg font-bold text-gray-800 mb-4 flex items-center">
                <FaPhone className="mr-2 text-green-600" />
                Утасны дугаар
              </h3>

              {/* Солонгос утас */}
              <div className="mb-6">
                <h4 className="text-sm md:text-base font-semibold text-gray-700 mb-3 flex items-center">
                  🇰🇷 Солонгос
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 1
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_korea_1}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_korea_1: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="010-1234-5678"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 2
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_korea_2}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_korea_2: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="010-9876-5432"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 3
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_korea_3}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_korea_3: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="010-5555-6666"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Монгол утас */}
              <div className="mb-6">
                <h4 className="text-sm md:text-base font-semibold text-gray-700 mb-3 flex items-center">
                  🇲🇳 Монгол
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 1
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_mongolia_1}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_mongolia_1: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="9999-8888"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 2
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_mongolia_2}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_mongolia_2: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="8888-7777"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 3
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_mongolia_3}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_mongolia_3: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="7777-6666"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Бусад утас */}
              <div>
                <h4 className="text-sm md:text-base font-semibold text-gray-700 mb-3 flex items-center">
                  🌏 Бусад (Зөвхөн Баримт 1-д харагдана)
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 1
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_other_1}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_other_1: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="+1-555-123-4567"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 2
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_other_2}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_other_2: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="+86-138-1234-5678"
                      disabled={!canEdit}
                    />
                  </div>
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-medium text-gray-600 mb-2">
                      Утас 3
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_phone_other_3}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_phone_other_3: e.target.value 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="+81-90-1234-5678"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* БАНКНЫ МЭДЭЭЛЭЛ */}
            <div className="border-2 border-blue-100 rounded-xl p-4 md:p-6 bg-gradient-to-br from-blue-50 to-white">
              <h3 className="text-base md:text-lg font-bold text-gray-800 mb-4 flex items-center">
                <FaUniversity className="mr-2 text-blue-600" />
                Банкны мэдээлэл
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {/* Банк 1 */}
                <div className="space-y-4">
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Банк 1 - Нэр
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_bank1_name}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_bank1_name: e.target.value 
                      }))}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Хаан банк"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Банк 1 - Данс
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_bank1_account}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_bank1_account: e.target.value 
                      }))}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base font-mono disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="5123 4567 8901"
                      disabled={!canEdit}
                    />
                  </div>
                </div>

                {/* Банк 2 */}
                <div className="space-y-4">
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Банк 2 - Нэр
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_bank2_name}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_bank2_name: e.target.value 
                      }))}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Голомт банк"
                      disabled={!canEdit}
                    />
                  </div>
                  
                  <div className="bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                    <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                      Банк 2 - Данс
                    </label>
                    <input
                      type="text"
                      value={receiptInfo.receipt_bank2_account}
                      onChange={(e) => setReceiptInfo(prev => ({ 
                        ...prev, 
                        receipt_bank2_account: e.target.value 
                      }))}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base font-mono disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="1234 5678 9012"
                      disabled={!canEdit}
                    />
                  </div>
                </div>
              </div>

              {/* Дансны эзэмшигч */}
              <div className="mt-4 bg-white p-3 md:p-4 rounded-lg border border-gray-200">
                <label className="block text-xs md:text-sm font-semibold text-gray-700 mb-2">
                  Дансны эзэмшигч
                </label>
                <input
                  type="text"
                  value={receiptInfo.receipt_account_holder}
                  onChange={(e) => setReceiptInfo(prev => ({ 
                    ...prev, 
                    receipt_account_holder: e.target.value 
                  }))}
                  className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm md:text-base disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Жимгүн Карго ХХК"
                  disabled={!canEdit}
                />
              </div>
            </div>

            {/* Анхааруулга */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 md:p-4 rounded">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-xs md:text-sm text-yellow-700">
                    <strong>Анхаар:</strong> Энэ мэдээлэл Баримт 1 болон Баримт 2 хоёуланд нь харагдах болно. 
                    <br />
                    <span className="font-semibold">Зөвхөн "Бусад" утас Баримт 1-д харагдана.</span>
                    <br />
                    Хадгалсны дараа хэвлэх баримтууд шинэчлэгдсэн мэдээллийг агуулна.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Button - ✅ Засах эрхтэй бол л харагдана */}
      {canEdit && (
        <div className="mt-6 flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 md:px-6 py-2.5 md:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-md hover:shadow-lg disabled:opacity-50 flex items-center text-sm md:text-base"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Хадгалж байна...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                Хадгалах
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Settings;